import { Component, ElementRef, Input, OnChanges, OnDestroy, OnInit, SimpleChanges, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs/internal/Subscription';
import { AppInfoService } from 'core/app-info/app-info.service';
import { ConfidenceToClose, ConfidenceToCloseNames } from 'src/app/modules/shared/entities/confidence-to-close.enum';
import { Quote, QuoteListResponse } from 'src/app/modules/pos/quotes/entities/quote-list.class';
import { AppLocalization } from 'src/app/modules/shared/entities/app-localization';
import { LastQuoteListItem } from 'src/app/modules/marketing/products/entities/quote-list-item.class';
import { QuoteDetails } from 'src/app/modules/shared/entities/quote-details.class';
import { StorageValues } from 'src/app/modules/shared/entities/storage-values.enum';
import { UtilsService } from 'core/utils/utils.service';
import { QuotesService } from '../../quotes-widget/quotes.service';

@Component({
  selector: 'app-open-quotes',
  templateUrl: './open-quotes.component.html',
  styleUrls: ['./open-quotes.component.scss'],
  standalone: false
})
export class OpenQuotesComponent implements OnInit, OnChanges, OnDestroy {
  @Input() accountManagerEmployeeId: string;
  @Input() isVisible: boolean;
  @ViewChild('quotesTable') quotesTable: ElementRef;

  private readonly subs = new Subscription();
  appStrings: AppLocalization;
  dateFormat: string;
  quotesList: LastQuoteListItem[];
  currencySymbol: string;
  quotesCount: number;
  loading = false;
  noResults = false;
  displayedColumns: string[] = [
    'accountName',
    'bpNumber',
    'confidenceToClosePercentageId',
    'quoteNumber',
    'expiredDate',
    'total',
    'view'
  ];
  confidenceToClose = ConfidenceToClose;
  confidenceToCloseNames = ConfidenceToCloseNames;

  constructor(
    private appInfoService: AppInfoService,
    private quotesService: QuotesService,
    private router: Router,
    private utilsService: UtilsService,
  ) { }

  ngOnInit() {
    this.appStrings = this.appInfoService.appStrings;
    this.dateFormat = this.appInfoService.dateFormat;
    this.currencySymbol = this.appInfoService.currencySymbol;
    this.getQuoteList();
  }

  ngOnChanges(changes: SimpleChanges) {
    const amChange = changes.accountManagerEmployeeId;
    if (amChange?.previousValue && amChange.previousValue !== amChange.currentValue) {
      this.getQuoteList(false);
    }
  }

  ngOnDestroy() {
    this.subs.unsubscribe();
  }

  /**
   * Getting quotes for an AM
   * @param filters filters
   */
  getQuoteList(scrollIntoView = true) {
    const cachedSearchValues = JSON.parse(sessionStorage.getItem(StorageValues.CachedFilters + StorageValues.LastTenQuotesKey));
    if (this.accountManagerEmployeeId) {
      this.loading = true;
      this.noResults = false;
      this.subs.add(this.quotesService.getLastQuotes(cachedSearchValues, this.accountManagerEmployeeId).subscribe((result) => {
        const allQuotes = cachedSearchValues ? this.reduceToTenResults(result) : result.quotes;
        this.quotesCount = allQuotes.length;
        this.noResults = this.quotesCount === 0;
        this.quotesList = allQuotes;
        this.loading = false;
        if (this.isVisible && scrollIntoView) {
          setTimeout(() => {
            this.scrollIntoView();
          });
        }
      }));
    }
  }

  /**
   * Redirecting to quote details page
   * @param quote quote id
   */
  goToQuoteDetails(quote: Quote) {
    this.router.navigate(['quotes', quote.id]);
  }

  /**
   * Reduces quotes results to 10 when there are more exact+partial results
   * @param quoteResponse array with exact and partial responses
   * @returns an array of 10 or less quotes
   */
  reduceToTenResults(quoteResponses: QuoteListResponse[]) {
    let lastTenQuotes: QuoteDetails[];
    const maxLength = 10;
    // Exact search quotes response
    const exactQuotesLength = quoteResponses[0].quotes.length;
    // Partial search quotes response
    const partialQuotesLength = quoteResponses[1].quotes.length;

    if (exactQuotesLength === maxLength) {
      lastTenQuotes = quoteResponses[0].quotes;
    } else if (exactQuotesLength < maxLength && partialQuotesLength) {
      const reducedPartial = quoteResponses[1].quotes.splice(0, maxLength - exactQuotesLength);
      lastTenQuotes = quoteResponses[0].quotes.concat(reducedPartial);
    } else {
      lastTenQuotes = quoteResponses[0].quotes;
    }

    return lastTenQuotes;
  }

  private scrollIntoView() {
    this.utilsService.scrollToElement(this.quotesTable.nativeElement, 145);
  }
}
